import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { generateUUID } from '@datadog/browser-core';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { delay, filter, tap } from 'rxjs';

import { InputComponent } from '@shared/components/input/input.component';

import { TooltipDirective } from '../../directives/tooltip.directive';

export interface ExtendableDropdownOption {
  disabled?: boolean;
  id: string;
  value: string;
}

interface RawSelectCompomnent {
  _handleArrowDown: (event: KeyboardEvent) => void;
  _handleArrowUp: (event: KeyboardEvent) => void;
}

const NEW_OPTION_ID = generateUUID();

const SEARCH_OPTION_ID = generateUUID();

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, ReactiveFormsModule, InputComponent, TooltipDirective],
  selector: 'aux-extendable-options-dropdown',
  styleUrl: './extendable-options-dropdown.component.scss',
  templateUrl: 'extendable-options-dropdown.component.html',
})
export class ExtendableOptionsDropdownComponent implements AfterViewInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);

  private filter = signal<string>('');

  items = input<ExtendableDropdownOption[]>([]);

  private dynamicOptions = computed<ExtendableDropdownOption[]>(() => {
    const filtered = this.items().filter((item) =>
      item.value?.trim().toLowerCase().includes(this.filter()?.trim().toLowerCase())
    );

    return filtered.some(
      (option) => option.value?.trim().toLowerCase() === this.filter()?.trim().toLowerCase()
    )
      ? filtered
      : ([
          ...filtered,
          this.filter()?.trim() && {
            id: NEW_OPTION_ID,
            value: this.filter(),
          },
        ].filter(Boolean) as ExtendableDropdownOption[]);
  });

  options = computed<ExtendableDropdownOption[]>(() => {
    return [
      {
        disabled: true,
        id: SEARCH_OPTION_ID,
        value: SEARCH_OPTION_ID,
      },
      ...this.dynamicOptions(),
    ];
  });

  @Output() addOption = new EventEmitter();
  @Output() changed = new EventEmitter<ExtendableDropdownOption | string | undefined>();
  @Input() classList: string = '';
  @Output() clickOut = new EventEmitter();
  @Output() confirm = new EventEmitter<string | undefined>();
  @Input() dropdownControl = new FormControl<null | string>(null);
  filterText = new FormControl<string>('');
  @Input() initialSearchTerm = '';
  isOpen = false;
  readonly newOptionId = NEW_OPTION_ID;
  @Input() onChangedReturnOption = false;
  @Input() openOnEnter = false;
  @Input() openOnInit = false;
  @Input() placeholder = 'Select or add new';
  @Input() resetValueOnEscapePress = false;
  @ViewChild(InputComponent) searchComponent!: InputComponent;
  readonly searchOptionId = SEARCH_OPTION_ID;
  @ViewChild(NgSelectComponent) selectCompomnent!: NgSelectComponent;
  @Input() showTooltips = true;
  readonly tooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -11,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      panelClass: 'expandable-options-tooltip',
    },
  ];
  @Input() trackByFn = this.defaultTrackByFn;

  private initialValue!: string;
  private openOnInitTimer!: number;
  private preventGlobalKeyEvents = false;

  ngAfterViewInit(): void {
    this.setIsOpenRecursively(this.openOnInit);
    this.initialValue = this.dropdownControl.value ?? '';
    this.filterText.setValue(this.initialSearchTerm);
    this.filterText.valueChanges
      .pipe(
        tap((value) => this.filter.set(value || '')),
        delay(0),
        filter(() => !!this.selectCompomnent.itemsList.markedItem),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.clearMarked);
  }

  ngOnDestroy(): void {
    clearTimeout(this.openOnInitTimer);
    this.changeDetectorRef.detectChanges();
  }

  private clearMarked = (): void => {
    setTimeout(() => {
      if (this.selectCompomnent.itemsList.markedItem) {
        this.selectCompomnent.itemsList.markedItem.selected = false;
      }

      this.selectCompomnent.detectChanges();
      this.changeDetectorRef.detectChanges();
    });
  };

  close(): void {
    this.setIsOpen(false);
  }

  private defaultTrackByFn(option: ExtendableDropdownOption): string {
    return option?.id;
  }

  detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }

  getItemValue(item: ExtendableDropdownOption): string {
    return item.id === NEW_OPTION_ID ? item.value + ' (New)' : item.value;
  }

  onChange(item?: ExtendableDropdownOption): void {
    this.setIsOpen(false);

    if (item?.id === NEW_OPTION_ID) {
      this.addOption.emit({
        id: item.value,
        value: item.value,
      });
      this.filterText.reset('');
    }

    this.changed.emit(this.onChangedReturnOption ? item : item?.value);
  }

  onClear(): void {
    this.searchComponent?.input?.nativeElement.focus();
  }

  @HostListener('document:click', ['$event'])
  public onClick(e: MouseEvent): void {
    const panel = document.querySelector('.ng-dropdown-panel.extendable-options-dropdown');
    const clickedInside =
      panel?.contains(e.target as Node | null) || this.elementRef.nativeElement.contains(e.target);

    if (!clickedInside) {
      this.clickOut.emit();
      this.setIsOpen(false);
    }
  }

  onClose(): void {
    this.selectCompomnent?.itemsList.unmarkItem();
  }

  @HostListener('document:keydown.escape', ['$event'])
  @HostListener('document:keydown.enter', ['$event'])
  public onGlobalKeyEnter({ key }: KeyboardEvent): void {
    if (!this.selectCompomnent?.isOpen && !this.preventGlobalKeyEvents) {
      if (this.resetValueOnEscapePress && key === 'Escape') {
        this.dropdownControl.setValue(this.initialValue ?? '');
      }

      this.confirm.emit();
    }
  }

  @HostListener('mousedown')
  public onMouseDown(): void {
    this.setIsOpen(!this.isOpen);
  }

  onOpen = () =>
    setTimeout(() => {
      const lastMarkedOrSelected =
        this.selectCompomnent?.itemsList.markedItem ||
        this.selectCompomnent?.itemsList.selectedItems?.[0];

      if (lastMarkedOrSelected) {
        this.clearMarked();
        setTimeout(() => this.selectCompomnent.itemsList.markItem(lastMarkedOrSelected));
      }
    });

  onSearchKeyDown(event: KeyboardEvent): void {
    this.preventGlobalKeyEvents = true;

    const { key } = event;

    if (key === 'Escape') {
      this.close();
    }

    if (key === 'Enter' && this.dynamicOptions().length) {
      this.selectCompomnent.itemsList.select(this.selectCompomnent.itemsList.markedItem);
      this.dropdownControl.setValue(this.selectCompomnent.itemsList.markedItem.value.value);
      this.onChange(this.selectCompomnent.itemsList.markedItem.value);
      this.close();
    }

    if (key === 'ArrowDown') {
      this.rawSelectComponentImpl._handleArrowDown(event);
    }

    if (key === 'ArrowUp') {
      this.rawSelectComponentImpl._handleArrowUp(event);
    }

    setTimeout(() => (this.preventGlobalKeyEvents = false));
  }

  optionMouseDown(opt: ExtendableDropdownOption, event: MouseEvent) {
    if (opt.value === this.dropdownControl.value) {
      event.stopPropagation();

      return;
    }
  }

  private get rawSelectComponentImpl(): RawSelectCompomnent {
    return this.selectCompomnent as unknown as RawSelectCompomnent;
  }

  setIsOpen(isOpen: boolean): void {
    this.isOpen = isOpen;

    if (isOpen) {
      this.onOpen();
    } else {
      this.onClose();
    }

    setTimeout(() => this.detectChanges());
  }

  private setIsOpenRecursively(isOpen: boolean): void {
    if (this.isOpen !== isOpen) {
      this.setIsOpen(this.openOnInit);
      this.openOnInitTimer = setTimeout(() => {
        this.setIsOpenRecursively(isOpen);
      }, 50) as unknown as number;
    }
  }
}
